import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { supabase } from "../lib/supabaseClient";
import Layout from "../components/Layout";
import { PageHeader, Btn, Modal, Field, Input, Select, Textarea, StatusBadge, EmptyState } from "../components/UI";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, Eye, Pencil, Trash2, Printer } from "lucide-react";

const STATUSES = ["Booked","Dispatched","In Transit","Arrived At Hub","Out For Delivery","Delivered","Cancelled"];

const blank = {
  lr_date: new Date().toISOString().slice(0,10),
  shipper_id: "", consignee_id: "", origin_branch_id: "", destination_branch_id: "",
  vehicle_id: "", agent_id: "", boxes: 1, weight: 0, description: "",
  invoice_number: "", invoice_value: 0, freight_charges: 0, other_charges: 0,
};

// Attach shipper/consignee/branch/vehicle/agent display names client-side,
// since we're querying Supabase tables directly instead of a pre-joined API.
function enrich(items, refs) {
  const byId = (arr) => Object.fromEntries((arr || []).map(x => [x.id, x]));
  const shippers = byId(refs.shippers), consignees = byId(refs.consignees),
        branches = byId(refs.branches), vehicles = byId(refs.vehicles), agents = byId(refs.agents);
  return items.map(it => ({
    ...it,
    shipper_name: shippers[it.shipper_id]?.company_name || "",
    consignee_name: consignees[it.consignee_id]?.company_name || "",
    origin_branch_name: branches[it.origin_branch_id]?.name || "",
    destination_branch_name: branches[it.destination_branch_id]?.name || "",
    vehicle_name: vehicles[it.vehicle_id]?.vehicle_number || "",
    agent_name: agents[it.agent_id]?.name || "",
  }));
}

export default function Consignments() {
  const [rawItems, setRawItems] = useState([]);
  const [refs, setRefs] = useState({ shippers: [], consignees: [], branches: [], vehicles: [], agents: [] });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank);
  const [viewItem, setViewItem] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackForm, setTrackForm] = useState({status:"Dispatched", location:"", remarks:""});
  const [filters, setFilters] = useState({status:"", start_date:"", end_date:"", origin_branch_id:"", destination_branch_id:"", agent_id:""});
  const [err, setErr] = useState("");
  const [params] = useSearchParams();

  const loadRefs = async () => {
    const [s, c, b, v, a] = await Promise.all([
      supabase.from("shippers").select("*"),
      supabase.from("consignees").select("*"),
      supabase.from("branches").select("*"),
      supabase.from("vehicles").select("*"),
      supabase.from("agents").select("*"),
    ]);
    setRefs({
      shippers: s.data || [], consignees: c.data || [], branches: b.data || [],
      vehicles: v.data || [], agents: a.data || [],
    });
  };

  const load = async () => {
    let q = supabase.from("consignments").select("*");
    if (filters.status) q = q.eq("status", filters.status);
    if (filters.origin_branch_id) q = q.eq("origin_branch_id", filters.origin_branch_id);
    if (filters.destination_branch_id) q = q.eq("destination_branch_id", filters.destination_branch_id);
    if (filters.agent_id) q = q.eq("agent_id", filters.agent_id);
    if (filters.start_date) q = q.gte("lr_date", filters.start_date);
    if (filters.end_date) q = q.lte("lr_date", filters.end_date);
    const lr = params.get("q");
    if (lr) q = q.ilike("lr_number", `%${lr}%`);
    const { data, error } = await q.order("created_at", { ascending: false });
    if (error) { setErr(error.message); return; }
    setRawItems(data || []);
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, params]);

  const items = enrich(rawItems, refs);

  const openCreate = () => { setEdit(null); setForm(blank); setErr(""); setOpen(true); };
  const openEdit = (it) => { setEdit(it); setForm(it); setErr(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = {...form, boxes:Number(form.boxes), weight:Number(form.weight),
                     invoice_value:Number(form.invoice_value), freight_charges:Number(form.freight_charges),
                     other_charges:Number(form.other_charges)};
    payload.vehicle_id = payload.vehicle_id || null;
    payload.agent_id = payload.agent_id || null;

    try {
      if (edit) {
        // Updates go direct to Supabase — no LR number involved, RLS protects tenant boundary
        payload.total_amount = payload.freight_charges + payload.other_charges;
        const { error } = await supabase.from("consignments").update(payload).eq("id", edit.id);
        if (error) throw new Error(error.message);
      } else {
        // Creation goes through the consignments-create Edge Function so the
        // LR number counter stays atomic (this is the one write that can't
        // safely happen as a direct client-side Supabase insert).
        await api.post(`/consignments-create`, payload);
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this consignment?")) return;
    const { error } = await supabase.from("consignments").delete().eq("id", id);
    if (error) { alert(error.message); return; }
    load();
  };

  const openView = async (it) => {
    setViewItem(it);
    const { data } = await supabase
      .from("tracking_updates")
      .select("*")
      .eq("consignment_id", it.id)
      .order("created_at", { ascending: true });
    setTimeline(data || []);
  };

  const addTracking = async (e) => {
    e.preventDefault();
    const { error: insErr } = await supabase.from("tracking_updates").insert({
      consignment_id: viewItem.id,
      company_id: viewItem.company_id,
      status: trackForm.status,
      location: trackForm.location,
      remarks: trackForm.remarks,
      updated_by: (await supabase.auth.getUser()).data.user?.email || "",
    });
    if (insErr) { alert(insErr.message); return; }
    await supabase.from("consignments").update({ status: trackForm.status }).eq("id", viewItem.id);

    const { data } = await supabase
      .from("tracking_updates")
      .select("*")
      .eq("consignment_id", viewItem.id)
      .order("created_at", { ascending: true });
    setTimeline(data || []);
    setTrackOpen(false);
    setTrackForm({status:"Dispatched", location:"", remarks:""});
    load();
  };

  const printLR = (it) => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>${it.lr_number}</title>
      <style>body{font-family:Arial;padding:32px;color:#111}
      h1{color:#1E3A8A;margin:0} .row{display:flex;gap:24px;margin:16px 0}
      .box{border:1px solid #cbd5e1;padding:12px;border-radius:6px;flex:1}
      table{width:100%;border-collapse:collapse;margin-top:16px} th,td{border:1px solid #cbd5e1;padding:8px;text-align:left;font-size:12px}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1E3A8A;padding-bottom:12px}
      </style></head><body>
      <div class="header"><div><h1>Ritik Cargo Carriers</h1><div>Lorry Receipt / Consignment Note</div></div>
      <div><div><b>LR No:</b> ${it.lr_number}</div><div><b>Date:</b> ${it.lr_date}</div></div></div>
      <div class="row"><div class="box"><b>Shipper</b><br>${it.shipper_name||""}</div>
      <div class="box"><b>Consignee</b><br>${it.consignee_name||""}</div></div>
      <div class="row"><div class="box"><b>From:</b> ${it.origin_branch_name||""}</div>
      <div class="box"><b>To:</b> ${it.destination_branch_name||""}</div></div>
      <table><tr><th>Boxes</th><th>Weight</th><th>Vehicle</th><th>Agent</th><th>Invoice</th><th>Inv Value</th></tr>
      <tr><td>${it.boxes}</td><td>${it.weight} kg</td><td>${it.vehicle_name||""}</td><td>${it.agent_name||""}</td><td>${it.invoice_number||""}</td><td>₹${it.invoice_value||0}</td></tr></table>
      <table><tr><th>Freight</th><th>Other</th><th>Total</th></tr>
      <tr><td>₹${it.freight_charges}</td><td>₹${it.other_charges}</td><td><b>₹${it.total_amount}</b></td></tr></table>
      <div style="margin-top:24px;font-size:11px;color:#666">Description: ${it.description||"-"}<br>Generated: ${new Date().toLocaleString()}</div>
      </body></html>`);
    w.document.close(); w.print();
  };

  return (
    <Layout>
      <PageHeader title="Consignments" subtitle="Bookings and shipment management"
        actions={<Btn data-testid="consignment-add" onClick={openCreate}><Plus size={16} className="inline -mt-0.5 mr-1"/>New Consignment</Btn>}/>

      <div className="lt-card p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <Field label="Status">
          <Select value={filters.status} onChange={e=>setFilters({...filters,status:e.target.value})} data-testid="filter-status">
            <option value="">All</option>{STATUSES.map(s=><option key={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="From Date"><Input type="date" value={filters.start_date} onChange={e=>setFilters({...filters,start_date:e.target.value})}/></Field>
        <Field label="To Date"><Input type="date" value={filters.end_date} onChange={e=>setFilters({...filters,end_date:e.target.value})}/></Field>
        <Field label="Origin">
          <Select value={filters.origin_branch_id} onChange={e=>setFilters({...filters,origin_branch_id:e.target.value})}>
            <option value="">All</option>{refs.branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Destination">
          <Select value={filters.destination_branch_id} onChange={e=>setFilters({...filters,destination_branch_id:e.target.value})}>
            <option value="">All</option>{refs.branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Agent">
          <Select value={filters.agent_id} onChange={e=>setFilters({...filters,agent_id:e.target.value})}>
            <option value="">All</option>{refs.agents.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
      </div>

      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

      {items.length === 0 ? <EmptyState title="No consignments found" action={<Btn onClick={openCreate}>Create one</Btn>}/> : (
        <div className="lt-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="consignments-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="text-left px-4 py-2.5">LR Number</th><th className="text-left">Date</th><th className="text-left">Shipper → Consignee</th><th className="text-left">Route</th><th className="text-left">Boxes/Wt</th><th className="text-left">Status</th><th className="text-right">Amount</th><th className="text-right px-4">Actions</th></tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{it.lr_number}</td>
                  <td className="text-slate-600">{it.lr_date}</td>
                  <td className="text-slate-700">{it.shipper_name} → {it.consignee_name}</td>
                  <td className="text-slate-600">{it.origin_branch_name} → {it.destination_branch_name}</td>
                  <td className="text-slate-600">{it.boxes} / {it.weight}kg</td>
                  <td><StatusBadge status={it.status}/></td>
                  <td className="text-right text-slate-800">₹{(it.total_amount||0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={()=>openView(it)} className="p-1.5 hover:bg-sky-100 rounded text-sky-700" data-testid={`view-${it.lr_number}`}><Eye size={15}/></button>
                    <button onClick={()=>printLR(it)} className="p-1.5 hover:bg-slate-200 rounded text-slate-700"><Printer size={15}/></button>
                    <button onClick={()=>openEdit(it)} className="p-1.5 hover:bg-sky-100 rounded text-sky-700"><Pencil size={15}/></button>
                    <button onClick={()=>del(it.id)} className="p-1.5 hover:bg-red-100 rounded text-red-600"><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal open={open} onClose={()=>setOpen(false)} title={edit ? `Edit ${edit.lr_number}` : "New Consignment"} size="xl">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="LR Date"><Input type="date" required value={form.lr_date} onChange={e=>setForm({...form,lr_date:e.target.value})}/></Field>
          <Field label="Shipper"><Select required value={form.shipper_id} onChange={e=>setForm({...form,shipper_id:e.target.value})} data-testid="con-shipper"><option value="">Select</option>{refs.shippers.map(s=><option key={s.id} value={s.id}>{s.company_name}</option>)}</Select></Field>
          <Field label="Consignee"><Select required value={form.consignee_id} onChange={e=>setForm({...form,consignee_id:e.target.value})} data-testid="con-consignee"><option value="">Select</option>{refs.consignees.map(s=><option key={s.id} value={s.id}>{s.company_name}</option>)}</Select></Field>
          <Field label="Origin Branch"><Select required value={form.origin_branch_id} onChange={e=>setForm({...form,origin_branch_id:e.target.value})} data-testid="con-origin"><option value="">Select</option>{refs.branches.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
          <Field label="Destination Branch"><Select required value={form.destination_branch_id} onChange={e=>setForm({...form,destination_branch_id:e.target.value})} data-testid="con-dest"><option value="">Select</option>{refs.branches.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
          <Field label="Vehicle"><Select value={form.vehicle_id} onChange={e=>setForm({...form,vehicle_id:e.target.value})}><option value="">— None —</option>{refs.vehicles.map(s=><option key={s.id} value={s.id}>{s.vehicle_number}</option>)}</Select></Field>
          <Field label="Agent"><Select value={form.agent_id} onChange={e=>setForm({...form,agent_id:e.target.value})}><option value="">— None —</option>{refs.agents.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
          <Field label="Boxes"><Input type="number" min="1" value={form.boxes} onChange={e=>setForm({...form,boxes:e.target.value})}/></Field>
          <Field label="Weight (kg)"><Input type="number" step="0.01" value={form.weight} onChange={e=>setForm({...form,weight:e.target.value})}/></Field>
          <Field label="Invoice Number"><Input value={form.invoice_number} onChange={e=>setForm({...form,invoice_number:e.target.value})}/></Field>
          <Field label="Invoice Value"><Input type="number" value={form.invoice_value} onChange={e=>setForm({...form,invoice_value:e.target.value})}/></Field>
          <Field label="Freight Charges"><Input type="number" value={form.freight_charges} onChange={e=>setForm({...form,freight_charges:e.target.value})} data-testid="con-freight"/></Field>
          <Field label="Other Charges"><Input type="number" value={form.other_charges} onChange={e=>setForm({...form,other_charges:e.target.value})}/></Field>
          <Field label="Description" className="md:col-span-3"><Textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></Field>
          {err && <div className="md:col-span-3 text-sm text-red-600">{err}</div>}
          <div className="md:col-span-3 flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Btn>
            <Btn type="submit" data-testid="con-save">{edit ? "Update" : "Create"}</Btn>
          </div>
        </form>
      </Modal>

      {/* View modal */}
      <Modal open={!!viewItem} onClose={()=>setViewItem(null)} title={viewItem ? `Shipment ${viewItem.lr_number}` : ""} size="lg">
        {viewItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Status</div><StatusBadge status={viewItem.status}/></div>
              <div><div className="text-xs text-slate-500">LR Date</div><div>{viewItem.lr_date}</div></div>
              <div><div className="text-xs text-slate-500">Boxes / Weight</div><div>{viewItem.boxes} / {viewItem.weight} kg</div></div>
              <div><div className="text-xs text-slate-500">Total</div><div className="font-bold">₹{(viewItem.total_amount||0).toLocaleString("en-IN")}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Shipper</div><div>{viewItem.shipper_name}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Consignee</div><div>{viewItem.consignee_name}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Origin</div><div>{viewItem.origin_branch_name}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Destination</div><div>{viewItem.destination_branch_name}</div></div>
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">Tracking Timeline</h4>
                <Btn variant="secondary" onClick={()=>setTrackOpen(true)} data-testid="add-tracking-btn"><Plus size={14} className="inline -mt-0.5 mr-1"/>Add Update</Btn>
              </div>
              <div data-testid="timeline">
                {timeline.length === 0 ? <div className="text-sm text-slate-500">No updates</div> :
                  timeline.map((t,i) => {
                    const last = i === timeline.length - 1;
                    return (
                      <div key={t.id} className={`timeline-step ${last?"current":"completed"}`}>
                        <div className="dot"></div>
                        <div className="flex items-baseline justify-between">
                          <div className="font-semibold text-slate-900">{t.status}</div>
                          <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-sm text-slate-600">{t.location} {t.remarks && `· ${t.remarks}`}</div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
            <div className="text-xs text-slate-500 border-t pt-3">
              Public tracking link: <Link to={`/track/${viewItem.lr_number}`} target="_blank" className="text-sky-700 hover:underline">/track/{viewItem.lr_number}</Link>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={trackOpen} onClose={()=>setTrackOpen(false)} title="Add Tracking Update" size="sm">
        <form onSubmit={addTracking} className="space-y-3">
          <Field label="Status"><Select value={trackForm.status} onChange={e=>setTrackForm({...trackForm,status:e.target.value})} data-testid="track-status">{STATUSES.map(s=><option key={s}>{s}</option>)}</Select></Field>
          <Field label="Location"><Input value={trackForm.location} onChange={e=>setTrackForm({...trackForm,location:e.target.value})} data-testid="track-location"/></Field>
          <Field label="Remarks"><Textarea value={trackForm.remarks} onChange={e=>setTrackForm({...trackForm,remarks:e.target.value})}/></Field>
          <div className="flex justify-end gap-2"><Btn variant="ghost" type="button" onClick={()=>setTrackOpen(false)}>Cancel</Btn><Btn type="submit" data-testid="track-save">Save</Btn></div>
        </form>
      </Modal>
    </Layout>
  );
}
